/* eslint-disable react/prop-types */
import React, { Component } from "react";
// @material-ui/core
import withStyles from "@material-ui/core/styles/withStyles";
import Icon from "@material-ui/core/Icon";
import LinearProgress from "@material-ui/core/LinearProgress"; // Import progress bar component
import moment from "moment"; // Import moment for date calculations
import GroupIcon from "@material-ui/icons/Group"; // Import group icon
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";

// core components
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";

import Goal from "views/Goal/Goal.jsx"; // Updated import
import GoalsCalendar from "./GoalsCalendar"; // Import the GoalsCalendar component
import { getGoals } from "services/goalsDataProvider"; // Updated import

import {
  grayColor,
  whiteColor,
  cardTitle as sharedCardTitle,
  cardSubtitle as sharedCardSubtitle,
  darkModeStyleBlocks
} from "assets/jss/material-dashboard-react.jsx";

const goalPalette = {
  bodyText: grayColor[2],
  softText: grayColor[14],
  softSurface: grayColor[12],
  borderSubtle: grayColor[8],
  borderCard: grayColor[15],
  surface: whiteColor
};

const styles = theme => ({
  pageHeader: {
    marginBottom: "18px"
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap"
  },
  headerControls: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    minWidth: "340px",
    width: "100%",
    maxWidth: "560px",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
      minWidth: "100%"
    }
  },
  searchField: {
    flex: "1 1 auto",
    maxWidth: "400px",
    [theme.breakpoints.down("xs")]: {
      maxWidth: "100%"
    }
  },
  calendarToggle: {
    marginLeft: "auto",
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0
    }
  },
  cardTitle: {
    ...sharedCardTitle,
    margin: 0,
    minHeight: "auto",
    color: "inherit",
    fontWeight: 600
  },
  cardSubtitle: {
    ...sharedCardSubtitle,
    marginTop: "6px",
    opacity: 0.85,
    color: "inherit"
  },
  bodyText: {
    color: goalPalette.bodyText
  },
  goalCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "8px"
  },
  goalNameCard: {
    color: goalPalette.bodyText,
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  goalTimeLeft: {
    color: goalPalette.softText,
    fontSize: "13px",
    whiteSpace: "nowrap"
  },
  goalMetaCard: {
    marginTop: "10px",
    marginBottom: "10px",
    background: goalPalette.softSurface,
    borderRadius: "10px",
    padding: "10px 12px",
    border: "1px solid " + goalPalette.borderSubtle,
    color: goalPalette.bodyText,
    fontSize: "13px"
  },
  goalCardContainer: {
    border: "1px solid " + goalPalette.borderCard
  },
  goalActionButtonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%"
  },
  calendarContainer: {
    textAlign: "center",
    padding: "40px 0",
    backgroundColor:
      theme.palette && theme.palette.type === "dark"
        ? "#1e1e2f"
        : "transparent",
    borderRadius: "8px"
  },
  ...(theme.palette && theme.palette.type === "dark"
    ? darkModeStyleBlocks.dashboard
    : {})
});

class Goals extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeGoal: props.match.params.goalName,
      goals: [],
      filterText: "",
      calendarView: false
    };
  }

  loadGoals = async () => {
    try {
      const data = await getGoals(this.props.axios, true);
      this.setState({ goals: data.results || [] });
    } catch (error) {
      this.setState({ goals: [] });
    }
  };

  componentDidMount() {
    if (
      this.state.activeGoal === ":goalName" || // Updated condition
      this.state.activeGoal == null
    ) {
      this.loadGoals();
    }
    // Restore user choice for calendar/list view
    const savedView = window.localStorage.getItem("goalsCalendarView");
    if (savedView === "calendar") {
      this.setState({ calendarView: true });
    } else if (savedView === "list") {
      this.setState({ calendarView: false });
    }
  }

  handleLinkClick = goalName => e => {
    e.preventDefault();
    this.setState({ activeGoal: goalName }); // Updated state update
  };

  handleProgressClick = goalId => e => {
    e.preventDefault();
    this.props.history.push(`/my-goals/${goalId}/progress`);
  };

  handleDetailsClick = goalId => e => {
    e.preventDefault();
    this.props.history.push(`/my-goals/${goalId}/details`);
  };

  handleFilterChange = e => {
    this.setState({ filterText: e.target.value });
  };

  handleViewSwitch = e => {
    const value = e.target.checked;
    this.setState({ calendarView: value });
    window.localStorage.setItem(
      "goalsCalendarView",
      value ? "calendar" : "list"
    );
  };

  renderGoals() {
    const { classes } = this.props;
    const { filterText, calendarView } = this.state;
    let goals = this.state.goals || [];

    // Filter goals by text
    if (filterText) {
      goals = goals.filter(
        goal =>
          goal.name.toLowerCase().includes(filterText.toLowerCase()) ||
          (goal.description &&
            goal.description.toLowerCase().includes(filterText.toLowerCase()))
      );
    }

    return (
      <React.Fragment>
        <div className={classes.pageHeader}>
          <div className={classes.headerRow}>
            <div>
              <h4 className={classes.cardTitle}>Goals</h4>
              <p className={classes.cardSubtitle}>
                Track goals, monitor progress, and switch between list and
                calendar views.
              </p>
            </div>
            <div className={classes.headerControls}>
              <TextField
                className={classes.searchField}
                label="Filter goals"
                variant="outlined"
                size="small"
                value={filterText}
                onChange={this.handleFilterChange}
              />
              <FormControlLabel
                className={classes.calendarToggle}
                control={
                  <Switch
                    checked={calendarView}
                    onChange={this.handleViewSwitch}
                    color="primary"
                  />
                }
                label="Calendar View"
              />
            </div>
          </div>
        </div>

        {/* Goals List or Calendar */}
        {calendarView ? (
          <div className={classes.calendarContainer}>
            <GoalsCalendar
              goals={goals}
              axios={this.props.axios}
              onGoalsUpdated={this.loadGoals}
            />
          </div>
        ) : (
          <GridContainer>
            {goals.map(goal => {
              const daysLeft = moment(goal.target_date).diff(moment(), "days");
              const localizedTargetDate = moment(goal.target_date).format("LL");
              const percentageCompletion =
                (goal.metadata && goal.metadata.percentage_completion) || 0;
              const canEdit =
                this.props.user != null &&
                this.props.user.user_id === goal.admin;
              return (
                <GridItem xs={12} sm={6} md={4} key={`goal-${goal.id}`}>
                  <Card className={classes.goalCardContainer}>
                    <CardBody>
                      <div className={classes.goalCardHeader}>
                        <div className={classes.goalNameCard}>
                          <Icon style={{ fontSize: "16px" }}>flag</Icon>
                          <h4 style={{ margin: 0 }}>{goal.name}</h4>
                          {goal.is_shared ? (
                            <GroupIcon style={{ fontSize: "1rem" }} />
                          ) : null}
                        </div>
                        <div className={classes.goalTimeLeft}>
                          {daysLeft > 0
                            ? `${daysLeft} days left`
                            : "Target date passed"}
                        </div>
                      </div>
                      <p className={classes.bodyText}>
                        {goal.description || "No description provided."}
                      </p>
                      <div className={classes.goalMetaCard}>
                        <strong>Target Date:</strong> {localizedTargetDate}
                      </div>
                      <LinearProgress
                        variant="determinate"
                        value={percentageCompletion}
                      />
                      <p className={classes.bodyText}>
                        <strong>Completion:</strong>{" "}
                        {percentageCompletion
                          ? `${percentageCompletion.toFixed(1)}%`
                          : "0%"}
                      </p>
                    </CardBody>
                    <CardFooter stats>
                      <div className={classes.goalActionButtonsContainer}>
                        <Button
                          type="button"
                          color="info"
                          size="sm"
                          onClick={this.handleProgressClick(goal.id)}
                        >
                          Progress
                        </Button>
                        <Button
                          type="button"
                          color="info"
                          size="sm"
                          onClick={this.handleDetailsClick(goal.id)}
                          disabled={!canEdit}
                        >
                          Details
                        </Button>
                      </div>
                    </CardFooter>
                  </Card>
                </GridItem>
              );
            })}
          </GridContainer>
        )}
      </React.Fragment>
    );
  }

  renderGoal() {
    // Updated method name
    return (
      <Goal
        history={this.props.history}
        axios={this.props.axios}
        name={this.state.activeGoal} // Updated property
      />
    );
  }

  render() {
    return this.renderGoals();
  }
}

export default withStyles(styles)(Goals);
